import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LOGO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck, Headphones, BarChart3 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); navigate("/"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex bg-white rounded-md shadow-sm mb-8 w-64 h-20 overflow-hidden items-center"><img src={LOGO} alt="Zeefa Technologies" className="w-full h-full object-cover object-center" data-testid="login-logo" /></div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Sign in</h1>
          <p className="text-muted-foreground mt-2 mb-8 text-sm">Zeefa Technologies CRM — reselling & tech support workspace.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm h-11" placeholder="you@zeefatechnologies.com" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-sm h-11" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-sm h-11 gap-2 group">
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-8">© {new Date().getFullYear()} Zeefa Technologies · delivering performance</p>
        </div>
      </div>

      <div className="hidden lg:flex relative overflow-hidden bg-[#0c0c0d] items-center">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px circle at 80% 20%, hsl(350 83% 41% / 0.55), transparent 60%), radial-gradient(500px circle at 20% 90%, hsl(350 83% 30% / 0.4), transparent 55%)" }} />
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(hsl(0 0% 100% / 0.03) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.03) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="relative px-14 text-white max-w-lg">
          <div className="flex bg-white rounded-md mb-8 w-60 h-20 overflow-hidden items-center"><img src={LOGO} alt="Zeefa Technologies" className="w-full h-full object-cover object-center" /></div>
          <h2 className="text-4xl font-heading font-bold tracking-tight leading-tight">One platform for sales, support & renewals.</h2>
          <p className="text-white/60 mt-4">Find any customer instantly, track every subscription and license, manage tech cases, and keep your team moving — all in one place.</p>
          <div className="mt-10 space-y-4">
            {[[ShieldCheck, "Unified customer & license records"], [Headphones, "Tech support cases end-to-end"], [BarChart3, "Live sales & renewal insights"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-white/80"><span className="h-9 w-9 rounded-sm bg-white/10 flex items-center justify-center"><Icon size={17} /></span> <span className="text-sm">{t}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
